import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import ExitIcon from "@mui/icons-material/HighlightOff";
import { Chart as ChartJS2 } from "react-chartjs-2";
import "chartjs-adapter-moment";
import ZoomPlugin, { resetZoom } from "chartjs-plugin-zoom";
import "./Chart.css";
import { LanguageContext } from "../context/LanguageContext";
import { useAppTheme } from "../context/ThemeContext";
import { getResponsiveWidgetScale } from "../utils/widgetResponsive";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineController,
  LineElement,
  Title,
  Legend,
  Tooltip,
  Filler,
} from "chart.js";

ChartJS.register(
  PointElement,
  LinearScale,
  TimeScale,
  CategoryScale,
  LineElement,
  LineController,
  Title,
  Legend,
  Filler,
  Tooltip,
  ZoomPlugin
);

const getOrCreateTooltip = (chart) => {
  const parent = chart.canvas.parentNode;
  let tooltipEl = parent.querySelector("div.custom-chart-tooltip");

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.className = "custom-chart-tooltip";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

const placeTooltipInsideChart = (chart, tooltipEl, caretX, caretY) => {
  const PAD = 10;

  const prevOpacity = tooltipEl.style.opacity;
  const prevVis = tooltipEl.style.visibility;
  tooltipEl.style.opacity = "1";
  tooltipEl.style.visibility = "hidden";
  tooltipEl.style.left = "0px";
  tooltipEl.style.top = "0px";

  const w = tooltipEl.offsetWidth || 0;
  const h = tooltipEl.offsetHeight || 0;

  tooltipEl.style.visibility = prevVis;
  tooltipEl.style.opacity = prevOpacity;

  const cw = chart.width;
  const ch = chart.height;

  let left = caretX + PAD;
  let top = caretY - h - PAD;

  if (left + w + PAD > cw) left = caretX - w - PAD;

  if (top < PAD) top = caretY + PAD;

  left = Math.max(PAD, Math.min(left, Math.max(PAD, cw - w - PAD)));
  top = Math.max(PAD, Math.min(top, Math.max(PAD, ch - h - PAD)));

  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
};

const externalTooltipHandler = (context, datastreams, showUnit, isRtl) => {
  const { chart, tooltip } = context;
  const tooltipEl = getOrCreateTooltip(chart);
  const datastreamList = Array.isArray(datastreams) ? datastreams : [];
  const escapeHtml = (value) =>
    String(value ?? "").replace(/[&<>"']/g, (char) => {
      const entities = {
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      };
      return entities[char] || char;
    });

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  tooltipEl.dir = isRtl ? "rtl" : "ltr";

  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    let innerHtml =
      '<div class="chart-tooltip__title">';

    titleLines.forEach((title) => (innerHtml += title));
    innerHtml += '</div><table class="chart-tooltip__table">';

    tooltip.dataPoints.forEach((dataPoint) => {
      const curveInfo = datastreamList.find((ds) => ds.pin === dataPoint.dataset.pin);
      const unit = showUnit !== false ? (curveInfo?.unit || "") : "";
      const rawDecimals = Number(curveInfo?.decimals);
      const decimals = Number.isInteger(rawDecimals)
        ? Math.max(0, Math.min(rawDecimals, 6))
        : 1;
      const rawValue = Number(dataPoint?.parsed?.y);
      const val =
        Number.isFinite(rawValue)
          ? rawValue.toLocaleString(undefined, {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            })
          : "N/A";
      const color = dataPoint.dataset.borderColor;
      const curveLabel = dataPoint?.dataset?.label || "Unnamed Curve";
      const safeLabel = escapeHtml(curveLabel);
      const safeValue = escapeHtml(val);
      const safeUnit = escapeHtml(unit);

      innerHtml += `
        <tr>
          <td class="chart-tooltip__leftCell">
            <span class="chart-tooltip__swatch" style="background:${color};"></span>
            <span class="chart-tooltip__label">${safeLabel}</span>
          </td>
          <td class="chart-tooltip__rightCell" style="color:${color};">
            <span class="chart-tooltip__value">${safeValue}</span>
            ${safeUnit ? `<span class="chart-tooltip__unit">${safeUnit}</span>` : ""}
          </td>
        </tr>`;
    });

    innerHtml += "</table>";
    tooltipEl.innerHTML = innerHtml;
  }

  placeTooltipInsideChart(chart, tooltipEl, tooltip.caretX, tooltip.caretY);

  tooltipEl.style.opacity = 1;
};

const ONE_HOUR_SEC = 60 * 60;
const ONE_DAY_SEC = 24 * ONE_HOUR_SEC;

const getTimeUnitConfig = (rangeWindowSec) => {
  const rangeSec = Number(rangeWindowSec);
  if (!Number.isFinite(rangeSec) || rangeSec <= 0) {
    return {
      unit: undefined,
    };
  }

  if (rangeSec <= ONE_DAY_SEC) {
    return {
      unit: "minute",
    };
  }

  return {
    unit: "day",
  };
};

const normalizeChartFontSize = (value, fallback) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) return fallback;
  return Math.max(6, Math.min(160, Math.round(numericValue)));
};

const Chart = ({
  title,
  transparentBackground,
  xData = [],
  min,
  max,
  curves = [],
  isLiveRange = false,
  rangeWindowSec,
  showUnit = true,
  id,
  dims = { width: 850, height: 550 },
  datastreams = [],
  draggable,
  labelFontSize,
  valueFontSize,
}) => {
  const { lang } = useContext(LanguageContext);
  const { mode } = useAppTheme();
  const isRtl = ["ar", "ur"].includes(lang);
  const [zoomIn, setZoomIn] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [cursorState, setCursorState] = useState('idle');
  const chartRef = useRef(null);
  const [zoomWindow, setZoomWindow] = useState(null);
  const zoomWindowRef = useRef(null);
  const zoomContextRef = useRef({
    id,
    isLiveRange,
    rangeWindowSec: Number(rangeWindowSec) || 0,
  });

  const toMs = (v) => {
    if (v == null) return null;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (v instanceof Date) return v.getTime();
    if (typeof v === "string") {
      const t = Date.parse(v);
      return Number.isNaN(t) ? null : t;
    }
    return null;
  };

  const effectiveRangeWindowSec = useMemo(() => {
    const explicit = Number(rangeWindowSec);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;

    const minMs = toMs(min);
    const maxMs = toMs(max);
    if (
      Number.isFinite(minMs) &&
      Number.isFinite(maxMs) &&
      maxMs > minMs
    ) {
      return Math.floor((maxMs - minMs) / 1000);
    }
    return 0;
  }, [rangeWindowSec, min, max]);

  const timeConfig = useMemo(
    () => getTimeUnitConfig(effectiveRangeWindowSec),
    [effectiveRangeWindowSec]
  );

  const cssVar = (name, fallback) =>
    getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim() || fallback;

  const responsiveTextScale = useMemo(
    () => getResponsiveWidgetScale(dims, { baseWidth: 320, baseHeight: 220, min: 0.82, max: 2.4 }),
    [dims]
  );
  const baseChartFontSize = Math.max(11, Math.min(24, 12 * responsiveTextScale));
  const labelFontSizePx = normalizeChartFontSize(labelFontSize, baseChartFontSize);
  const valueFontSizePx = normalizeChartFontSize(valueFontSize, baseChartFontSize);
  const chartTitleFontSizePx = normalizeChartFontSize(
    labelFontSize,
    Math.max(14, Math.min(42, 18 * responsiveTextScale))
  );

  const syncZoomWindow = useCallback((chart) => {
    const xScale = chart?.scales?.x;
    if (!xScale) return;

    const nextMin = Number(xScale.min);
    const nextMax = Number(xScale.max);

    if (!Number.isFinite(nextMin) || !Number.isFinite(nextMax) || nextMax <= nextMin) {
      zoomWindowRef.current = null;
      setZoomWindow(null);
      setZoomIn(false);
      return;
    }

    const nextWindow = { min: nextMin, max: nextMax };
    const prevWindow = zoomWindowRef.current;
    if (prevWindow?.min === nextWindow.min && prevWindow?.max === nextWindow.max) {
      if (!zoomIn) setZoomIn(true);
      return;
    }

    zoomWindowRef.current = nextWindow;
    setZoomWindow(nextWindow);
    setZoomIn(true);
  }, [zoomIn]);

  const options = useMemo(
    () => {
      const mutedFallback = mode === "light" ? "#64748b" : "#64748b";
      const legendFallback = mode === "light" ? "#64748b" : "#94a3b8";
      const gridXFallback = mode === "light" ? "rgba(148, 163, 184, 0.24)" : "rgba(51, 65, 85, 0.3)";
      const gridYFallback = mode === "light" ? "rgba(148, 163, 184, 0.38)" : "rgba(51, 65, 85, 0.6)";

      return {
        responsive: true,
        animation: false,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        scales: {
          x: {
            type: "time",
            min: zoomWindow?.min ?? min,
            max: zoomWindow?.max ?? max,
            ticks: {
              color: cssVar("--chart-muted", mutedFallback),
              maxRotation: 0,
              autoSkip: true,
              maxTicksLimit: 8,
              font: { size: labelFontSizePx },
            },
            grid: { color: cssVar("--chart-grid-x", gridXFallback), drawTicks: false },
            time: {
              unit: timeConfig.unit,
              tooltipFormat: "MMM D, YYYY h:mm:ss a",
              displayFormats: {
                millisecond: "h:mm:ss.SSS a",
                second: "h:mm:ss a",
                minute: "h:mm a",
                hour: "h:mm a",
                day: "MMM D",
                week: "MMM D, YYYY",
                month: "MMM YYYY",
                quarter: "[Q]Q YYYY",
                year: "YYYY",
              },
            },
          },
          y: {
            type: "linear",
            position: isRtl ? "right" : "left",
            beginAtZero: false,
            grace: "10%",
            ticks: {
              color: cssVar("--chart-legend", legendFallback),
              callback: (value) => Number(value).toLocaleString(),
              font: { size: valueFontSizePx },
            },
            grid: { color: cssVar("--chart-grid-y", gridYFallback) },
          },
        },
        plugins: {
          legend: {
            position: "top",
            align: "end",
            rtl: isRtl,
            textDirection: isRtl ? "rtl" : "ltr",
            labels: {
              color: cssVar("--chart-legend", legendFallback),
              usePointStyle: true,
              pointStyle: "rectRounded",
              padding: 20,
              font: { size: labelFontSizePx },
            },
          },
          zoom: {
            pan: {
              enabled: !draggable,
              mode: "x",
              onPanComplete: ({ chart }) => syncZoomWindow(chart),
            },
            zoom: {
              mode: "x",
              wheel: { enabled: true, modifierKey: "ctrl" },
              drag: { enabled: true, modifierKey: "alt" },
              onZoomStart: () => setZoomIn(true),
              onZoomComplete: ({ chart }) => syncZoomWindow(chart),
            },
          },
          tooltip: {
            enabled: false,
            rtl: isRtl,
            textDirection: isRtl ? "rtl" : "ltr",
            external: (context) => externalTooltipHandler(context, datastreams, showUnit, isRtl),
          },
        },
      };
    },
    [min, max, datastreams, draggable, isRtl, labelFontSizePx, mode, showUnit, timeConfig, valueFontSizePx, zoomWindow, syncZoomWindow]
  );



  const xSig = `${xData?.length || 0}:${xData && xData.length ? xData[xData.length - 1] : ""}`;
  const curvesSig = (curves || [])
    .map((c) => `${c?.yLabel || ""}:${c?.yData?.length || 0}:${c?.yData && c.yData.length ? c.yData[c.yData.length - 1] : ""}`)
    .join("|");

  const data = useMemo(() => {
    const palette = ["#3b82f6", "#ef4444", "#10b981", "#f59e0b", "#8b5cf6"];
    const datasets = (curves || []).map((curve, idx) => ({
      label: curve.yLabel,
      data: (xData || []).map((x, i) => ({ x, y: curve?.yData?.[i] })),
      borderColor: curve.bdColor || palette[idx % palette.length],
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 5,
      tension: 0.3,
      pin: curve.pin,
    }));

    return { datasets };
  }, [curves, xData, xSig, curvesSig]);

  const chartInstanceKey = useMemo(() => {
    if (isLiveRange) {
      return `${id}|live|${effectiveRangeWindowSec}`;
    }

    const firstX = xData?.length ? xData[0] : "";
    const lastX = xData?.length ? xData[xData.length - 1] : "";
    return `${id}|history|${effectiveRangeWindowSec}|${firstX}|${lastX}`;
  }, [id, isLiveRange, effectiveRangeWindowSec, xData]);

  useEffect(() => {
    const nextContext = {
      id,
      isLiveRange,
      rangeWindowSec: Number(rangeWindowSec) || 0,
    };
    const prevContext = zoomContextRef.current;

    const shouldResetZoom =
      prevContext.id !== nextContext.id ||
      prevContext.isLiveRange !== nextContext.isLiveRange ||
      prevContext.rangeWindowSec !== nextContext.rangeWindowSec;

    zoomContextRef.current = nextContext;

    if (!shouldResetZoom) return;

    zoomWindowRef.current = null;
    setZoomWindow(null);
    setZoomIn(false);

    if (chartRef.current && (zoomWindowRef.current || zoomIn)) {
      resetZoom(chartRef.current);
    }
  }, [id, isLiveRange, rangeWindowSec, zoomIn]);

  const handleResetZoom = () => {
    if (chartRef.current) {
      resetZoom(chartRef.current);
      zoomWindowRef.current = null;
      setZoomWindow(null);
      setZoomIn(false);
    }
  };

  const handleDragStart = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.x;
    const offsetY = e.clientY - rect.y;

    let dragData = `chart;${id};${offsetX};${offsetY};`;
    dragData += id && id.toString().startsWith("base") ? "add" : "move";
    e.dataTransfer.setData("text/plain", dragData);
  };

  const cursorClass = draggable
    ? (cursorState === 'grabbing' ? 'chart--grabbing' : 'chart--grab')
    : 'chart--default';

  const styleVars = useMemo(() => {
    const w = typeof dims?.width === 'number' ? `${dims.width}px` : (dims?.width || '850px');
    const h = typeof dims?.height === 'number' ? `${dims.height}px` : (dims?.height || '550px');
    const themeBackground = cssVar("--header-background", mode === "light" ? "#F6FFFB" : "#131b2f");

    const bg =
      typeof transparentBackground === 'string' && transparentBackground.trim() !== ''
        ? transparentBackground
        : (transparentBackground
          ? 'transparent'
          : themeBackground);

    return {
      '--chart-width': w,
      '--chart-height': h,
      '--chart-bg-effective': bg,
      '--chart-pointer-events': draggable ? 'none' : 'auto',
      '--chart-title-size': `${chartTitleFontSizePx}px`,
      '--chart-help-size': `${normalizeChartFontSize(labelFontSize, Math.max(11, Math.min(20, 12 * responsiveTextScale)))}px`,
      '--chart-empty-size': `${normalizeChartFontSize(valueFontSize, Math.max(12, Math.min(24, 14 * responsiveTextScale)))}px`,
      '--chart-icon-size': `${Math.max(18, Math.min(34, 22 * responsiveTextScale))}px`,
    };
  }, [chartTitleFontSizePx, dims, draggable, labelFontSize, mode, responsiveTextScale, transparentBackground, valueFontSize]);

  return (
    <div
      className={[
        "widget-container",
        "chart-container",
        transparentBackground === true ? "chart--transparent" : "",
        cursorClass,
      ].filter(Boolean).join(" ")}
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onMouseDown={() => draggable && setCursorState('grabbing')}
      onMouseUp={() => draggable && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      style={styleVars}
    >
      <div className="chart-header">
        <h3 className="chart-title">{title}</h3>

        <div className="chart-header__actions">
          {!draggable &&
            (zoomIn ? (
              <ExitIcon onClick={handleResetZoom} className="chart-resetZoomIcon" />
            ) : (
              <div className="chart-help">
                <InfoOutlined
                  onMouseEnter={() => setHelpOpen(true)}
                  onMouseLeave={() => setHelpOpen(false)}
                  className="chart-infoIcon"
                />
                <div className={["chart-helpMenu", helpOpen ? "chart-helpMenu--open" : ""].filter(Boolean).join(" ")}>
                  <p className="chart-helpMenu__row">
                    <span className="chart-helpMenu__dot">•</span> CTRL + Wheel to Zoom
                  </p>
                  <p className="chart-helpMenu__row">
                    <span className="chart-helpMenu__dot">•</span> ALT + Drag to Zoom Area
                  </p>
                </div>
              </div>
            ))}
        </div>
      </div>

      <div className="chart-body">
        {(xData.length > 0 || isLiveRange) ? (
          <>
            <ChartJS2 key={chartInstanceKey} ref={chartRef} type="line" data={data} options={options} />
            {xData.length === 0 ? (
              <div className="chart-emptyOverlay">
                Waiting for sensor data...
              </div>
            ) : null}
          </>
        ) : (
          <div className="chart-emptyText">
            Waiting for sensor data...
          </div>
        )}
      </div>
    </div>
  );
};

export default Chart;
